import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs";
import { KeyHandlerService, RepositoryService, SecurityService, ToastService } from "@tylertech/core-module";
import { IMenuOption } from "@tylertech/forge";
import { DialogService, ForgeIconButtonModule, ForgeIconModule, ForgeMenuModule, ForgeTooltipModule, IDialogOptions } from "@tylertech/forge-angular";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { gridLayoutType } from "../../../constants";
import { IGridLayout, ModifierKey } from "@tylertech/tyler-api-types";
import { SaveDialogComponent } from "./saveDialog.component";
import { ActivatedRoute, NavigationExtras, Params, Router } from "@angular/router";

@Component({
    selector: "grid-query",
    templateUrl: "./gridQuery.tmpl.html",
    imports: [
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeMenuModule,
        ForgeTooltipModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class GridQueryComponent implements OnInit
{
    readonly gridId = input<string>();
    readonly model = input<any>();

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly dialogService = inject(DialogService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $odataFilterFactoryService = inject(ODataFilterFactoryService);
    private readonly $toastService = inject(ToastService);
    private readonly $securityService = inject(SecurityService);
    private readonly $repositoryService = inject(RepositoryService);

    readonly compactMode: boolean = this.$securityService.IsCompactMode;

    private readonly currentFilter = toSignal(this.activatedRoute.queryParamMap.pipe(map(paramMap => paramMap.get("$filter"))), { initialValue: null });
    private readonly currentQueryFilters = signal<IGridLayout[]>([]);

    readonly menuOptions = computed<IMenuOption[]>(() =>
    {
        const layouts = this.currentQueryFilters();
        const options: IMenuOption[] = [];

        const subMenuItems = (): IMenuOption[] => layouts.map(layout => <IMenuOption>{
            value: layout.Name,
            label: layout.Name
        });

        if (layouts.length > 0)
        {
            options.push(<IMenuOption>{
                value: "load",
                label: "Load",
                options: subMenuItems()
            });
        }

        options.push(<IMenuOption>{
            value: "save",
            label: "Save",
            disabled: !this.currentFilter()
        });

        if (layouts.length > 0)
        {
            options.push(<IMenuOption>{
                value: "delete",
                label: "Delete",
                options: subMenuItems()
            });
        }

        return options;
    });

    ngOnInit(): void
    {
        this.setSavedQueryOptions();
    }

    menuOpened(e): void
    {
        e.target.activateFirstOption();
    }

    setSavedQueryOptions(): void
    {
        this.load().then(result => this.currentQueryFilters.set(result));
    }

    loadQueryItemSelected(e): void
    {
        if (e.detail.parentValue === "load")
        {
            this.loadSingle(e.detail.value)
                .then(result =>
                {
                    if (result)
                        this.applyFilter(result.Layout);
                });
        }
        else if (e.detail.parentValue === "delete")
        {
            this.loadSingle(e.detail.value)
                .then(result =>
                {
                    if (result)
                    {
                        result.SerializedRowState = "Deleted";
                        this.$repositoryService
                            .Save({
                                Item1: gridLayoutType,
                                Item2: Utilities.stringify(result),
                            }).then(_ =>
                            {
                                this.showToast(`Load Query Deleted - ${result.Name}`);
                                this.setSavedQueryOptions();
                            });
                    }
                });
        }
        else //save
        {
            const options: IDialogOptions = { preset: 'dialog' };
            const dialogRef = this.dialogService.open(SaveDialogComponent, { options });
            dialogRef.afterClosed.subscribe(name => this.save(name));
        }
    }

    private loadSingle(queryName: string): Promise<IGridLayout>
    {
        const match = this.currentQueryFilters().find(x => x.Name === queryName);
        if (match)
            return Promise.resolve(match);
        return this.load(queryName).then(result =>
        {
            if (result.length > 0)
                return result[0];
            return null;
        });
    }

    private load(queryName?: string): Promise<IGridLayout[]>
    {
        const userId = this.$securityService.principle.UserId;
        const odata = this.$odataFilterFactoryService
            .createBuilder()
            .filterEQ("UserId", userId)
            .filterEQ("GridId", this.gridId())
            .filterEQ("QueryOnly", true);
        if (queryName)
            odata.filterEQ("Name", queryName);
        return this.$repositoryService.Fetch(gridLayoutType, odata.build()).then((result: IGridLayout[]) => result);
    }

    private applyFilter(filter: string): void
    {
        const previous = this.$odataFilterFactoryService.parseOdataFilter(this.currentFilter());
        const current = this.$odataFilterFactoryService.parseOdataFilter(filter);

        this.updateQueryParams({ "$filter": filter });

        const model = this.model();
        if (!model)
            return;

        // Clear the previous filter
        for (const field of Object.keys(previous))
        {
            if (!(field in current))
                model[field] = null;
        }

        for (const [field, value] of Object.entries(current))
            model[field] = value;

        this.$keyHandlerService.doCallback("Enter", ModifierKey.Ctrl);
    }

    updateQueryParams(queryParams: Params): void
    {
        const extras: NavigationExtras = {
            relativeTo: this.activatedRoute,
            queryParams,
            queryParamsHandling: "merge"
        };
        this.router.navigate([], extras);
    }

    private save(name: string): void
    {
        if (!name)
            return;
        const newLayout = <IGridLayout>{
            UserId: this.$securityService.principle.UserId,
            GridId: this.gridId(),
            IsDefault: false,
            IsPublic: false,
            Layout: this.currentFilter(),
            Name: name,
            QueryOnly: true,
            SerializedRowState: "Added"
        };
        this.$repositoryService
            .Save({
                Item1: gridLayoutType,
                Item2: Utilities.stringify(newLayout),
            })
            .then(_x =>
            {
                this.showToast(`Load Query Saved - ${name}`);
                this.setSavedQueryOptions();
            });
    }

    showToast(message: string): void
    {
        this.$toastService.showToast(message, 2000);
    }
}
